import Map from "@commons/Map";
import { usePage } from "@hooks/usePage";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { AppError } from "@utils/errors/appError";
import {
  SAMPLE_TYPE_COLORS,
  SAMPLE_TYPE_LABELS,
  type SampleType,
} from "@typings/entities/samples/enums";
import type { SampleGeoJsonProperties } from "@services/samples/getGeoJson/type";
import { useGetSamplesGeoJson } from "@services/samples/getGeoJson";
import type { SamplesFilters } from "@services/samples/list/type";
import SamplePopup from "./Popup";
import { mapColorToSamples } from "@utils/translator/samples";
import { usePermissions } from "@hooks/usePermissions";

const LEGENDS = Object.entries(SAMPLE_TYPE_LABELS).map(([key, label]) => ({
  label,
  color: SAMPLE_TYPE_COLORS[key as SampleType].tw,
}));

function MapView({ appliedFilters }: { appliedFilters: SamplesFilters }) {
  const { openModal } = usePage();
  const { can } = usePermissions();

  const { data, isLoading, error } = useGetSamplesGeoJson(appliedFilters);

  const processedData = data ? mapColorToSamples(data) : null;

  useEffect(() => {
    if (error) {
      const isAppError = error instanceof AppError;

      const title = isAppError
        ? error.message
        : "Erro ao carregar as Amostras! Tente novamente!";
      toast.error(title, { autoClose: 6000 });
    }
  }, [error]);

  const canCreateSample = can("create", "samples");

  return (
    <div className="w-full h-[calc(100vh-12rem)] flex flex-col pt-2 gap-6">
      <div className="flex-1 bg-white rounded-lg shadow-md p-4 min-h-0">
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <Map.Root>
              {canCreateSample && (
                <Map.Button onClick={() => openModal("newSample")}>
                  Nova Amostra
                </Map.Button>
              )}
              <Map.Draw<SampleGeoJsonProperties>
                geoJsonData={processedData}
                popupComponent={SamplePopup}
                canCreate={canCreateSample}
                modalToOpen="newSample"
                coordinatesButtonLabel="Nova Amostra"
              />
              <Map.Footer legends={LEGENDS} />
            </Map.Root>
          </>
        )}
      </div>
    </div>
  );
}

export default MapView;
